// "Verso holds this; the systems carrying the calls don't."
//
// Replaces the red "Push N pending changes" button that sat on the customer header. That
// button had the count but not the subject: it could say a customer had three unpushed
// changes and not which of their four numbers they were on, and pressing it swept every
// number, extension and device the customer had. This says which line, what specifically,
// and offers the build for that one line.
//
// TWO STATES THAT ARE NOT THE SAME, and the reason this isn't just a number in a circle:
//
//   * work a build would do        -> `buildable`, and the button is worth pressing
//   * work an operator must do     -> `blocked`, and pressing Build would be refused
//
// A line whose only outstanding item is an unregistered 911 address is in the second
// state. Rendering that as "2 pending [Build]" invites a press that the planner declines,
// and an operator who learns the button sometimes does nothing stops trusting it.
//
// And `unknown` is neither: it means a host could not be reached, so the count is a floor
// rather than a total. A tick over "we couldn't check" is the failure mode this codebase
// keeps re-learning (feedback_verify_dont_trust_writes), so it never renders as one.
//
// `notes` are the fourth thing, added after this shipped wrong. Line 134 (+17208302800)
// showed "✓ Everything Verso holds for this line has reached the PBX, the carrier and the
// handset" while GDMS held no SIP account for the number — because no handset was bound, so
// no GDMS step was ever planned. Nothing was out of sync and the sentence was still false.
// An all-clear may only name the systems it actually reached, and anything true-but-absent
// is stated rather than papered over. Notes do not raise the count: a softphone line has no
// handset by design, and an amber badge on a correct line is how a badge stops being read.

const SYSTEM_LABEL = { pbx: 'PBX', commio: 'carrier', gdms: 'GDMS', verso: 'Verso' };

// A note, in as few words as a table cell allows. The full sentence is the tooltip.
const NOTE_CHIP = { no_handset: 'no phone', voicemail_off: 'voicemail off' };
const noteChip = (note) => NOTE_CHIP[note.kind] || note.kind;

/** The compact form, for a table row. */
export function PendingChip({ pending, className = '' }) {
  const count = pending?.count ?? 0;
  const unknown = pending?.unknown?.length ? pending.unknown : null;

  const notes = pending?.notes?.length ? pending.notes : null;

  if (!count) {
    // Nothing outstanding — but say so differently when we could not ask the PBX, because
    // "agrees" and "unverified" look identical here otherwise.
    if (unknown) {
      return (
        <span
          title={`Couldn’t reach the ${unknown.map((u) => SYSTEM_LABEL[u] || u).join(', ')} to check`}
          className={`inline-flex items-center gap-1 text-xs text-gray-400 ${className}`}
        >
          ? unverified
        </span>
      );
    }
    return (
      <span
        title={notes ? notes.map((n) => `• ${n.why}`).join('\n') : undefined}
        className={`inline-flex items-center gap-1 text-xs text-green-700 ${className}`}
      >
        ✓ in sync
        {/* Not amber — nothing is out of sync. But a line with no handset bound must not
            look identical to one whose handset is provisioned and registered. */}
        {notes && <span className="text-gray-500">· {notes.length === 1 ? noteChip(notes[0]) : `${notes.length} notes`}</span>}
      </span>
    );
  }

  return (
    <span
      title={(pending.items || []).map((i) => `• ${i.why}`).join('\n')}
      className={'inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs '
               + `font-medium text-amber-900 ${className}`}
    >
      ⚠ {count} pending{unknown ? '+' : ''}
    </span>
  );
}

/**
 * The full form, for the line page.
 *
 * NO BUTTON. This carried an "Apply to this line" button until 2026-08-15; Kevin
 * asked for it out. It ran `setRun('provision')` — the identical call behind "Save
 * and Apply" in the action bar and "Build it now…" in the 911 notice, so the page
 * offered one verb under three different names, one of which ("Save and Apply")
 * does not save anything. This panel now REPORTS and the action bar ACTS.
 */
export default function PendingChanges({ pending }) {
  const items = pending?.items || [];
  const unknown = pending?.unknown?.length ? pending.unknown : null;

  if (!items.length) {
    // NOTHING TO SAY IS SAID BY SAYING NOTHING (2026-08-19).
    //
    // Kevin: the "✓ Everything Verso holds for this line has reached the PBX, the carrier and the
    // phone" line and the "— Voicemail is switched off for this line…" note under it "can go away.
    // the space can still be used for messages and alerts."
    //
    // A green all-clear on every healthy line is a card an operator learns to scroll past, and the
    // notes beneath it were restating settled facts — voicemail being off is a choice, and the
    // Voicemail tab's own switch now shows it. The status badges beside the number carry the
    // at-a-glance answer.
    //
    // WHAT IS KEPT is the one case where silence would be a lie: a system we could not reach. That
    // is not a clean bill of health, and nothing else on the page says so.
    if (unknown) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
          <p className="text-sm text-gray-600">
            Nothing outstanding in Verso’s own records, but the{' '}
            {unknown.map((u) => SYSTEM_LABEL[u] || u).join(', ')} could not be reached — so this
            is not a clean bill of health, just everything we could check.
          </p>
        </div>
      );
    }
    return null;
  }

  const buildable = pending.buildable ?? items.filter((i) => i.fixedByBuild).length;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-amber-900">
            {items.length} pending change{items.length === 1 ? '' : 's'}
          </h2>
          <p className="mt-0.5 text-xs text-amber-800">
            {buildable === items.length
              ? (items.length === 1 ? 'Building this line applies it.' : 'Building this line applies all of them.')
              : buildable > 0
                ? `Building applies ${buildable}; the rest need a decision first.`
                : 'None of these can be applied by a build — each needs a decision first.'}
          </p>
        </div>
      </div>

      <ul className="mt-3 space-y-1.5">
        {items.map((i, n) => (
          <li key={`${i.kind}-${i.ref ?? n}`} className="flex gap-2 text-sm text-amber-900">
            <span
              title={i.fixedByBuild ? 'A build applies this' : 'Needs a decision before a build can run'}
              className="mt-0.5 flex-shrink-0 text-xs"
            >
              {i.fixedByBuild ? '↻' : '⚑'}
            </span>
            <span>
              {i.why}
              <span className="ml-1 text-xs text-amber-700">({SYSTEM_LABEL[i.system] || i.system})</span>
            </span>
          </li>
        ))}
      </ul>

      {unknown && (
        <p className="mt-3 text-xs text-amber-800">
          The {unknown.map((u) => SYSTEM_LABEL[u] || u).join(', ')} could not be reached, so there
          may be more than this.
        </p>
      )}
    </div>
  );
}
